<template>
  <div class="working-capital-container" ref="containerRef">
    <div class="input-area">
      <div class="input-row">
        <div class="input-group">
          <label class="input-label">应收帐款年周转次数</label>
          <div class="input-with-unit">
            <input
              type="number"
              v-model.number="inputData.accountsReceivableTurnover"
              class="input-field"
              placeholder="请输入应收帐款周转次数"
              step="0.1"
              min="0"
              @change="updateWorkingCapital"
            />
            <span class="input-unit">次/年</span>
          </div>
        </div>
        <div class="input-group">
          <label class="input-label">应付帐款年周转次数</label>
          <div class="input-with-unit">
            <input
              type="number"
              v-model.number="inputData.accountsPayableTurnover"
              class="input-field"
              placeholder="请输入应付帐款周转次数"
              step="0.1"
              min="0"
              @change="updateWorkingCapital"
            />
            <span class="input-unit">次/年</span>
          </div>
        </div>
        <div class="input-group">
          <label class="input-label">存货年周转次数</label>
          <div class="input-with-unit">
            <input
              type="number"
              v-model.number="inputData.inventoryTurnover"
              class="input-field"
              placeholder="请输入存货周转次数"
              step="0.1"
              min="0"
              @change="updateWorkingCapital"
            />
            <span class="input-unit">次/年</span>
          </div>
        </div>
        <div class="input-group">
          <label class="input-label">现金年周转次数</label>
          <div class="input-with-unit">
            <input
              type="number"
              v-model.number="inputData.cashTurnover"
              class="input-field"
              placeholder="请输入现金周转次数"
              step="0.1"
              min="0"
              @change="updateWorkingCapital"
            />
            <span class="input-unit">次/年</span>
          </div>
        </div>
        <div class="input-group">
          <label class="input-label">流动资金贷款比例</label>
          <div class="input-with-unit">
            <input
              type="number"
              v-model.number="inputData.loanRatio"
              class="input-field"
              placeholder="请输入流动资金贷款比例"
              step="0.1"
              min="0"
              max="100"
              @change="updateWorkingCapital"
            />
            <span class="input-unit">%</span>
          </div>
        </div>
        <div class="input-group">
          <label class="input-label">流动资金贷款利率</label>
          <div class="input-with-unit">
            <input
              type="number"
              v-model.number="inputData.loanInterestRate"
              class="input-field"
              placeholder="请输入流动资金贷款利率"
              step="0.01"
              min="0"
              max="100"
              @change="updateWorkingCapital"
            />
            <span class="input-unit">%</span>
          </div>
        </div>
      </div>
    </div>
    <div class="table-wrapper">
      <div class="table-scroll-area">
        <table class="working-capital-table">
          <thead>
            <tr>
              <th class="seq-col">序号</th>
              <th class="item-col">项目</th>
              <th class="total-col">合计</th>
              <th
                class="year-col"
                v-for="index in calculationPeriod"
                :key="'year-header-' + index"
                style="text-align: center"
              >
                {{ index }}
              </th>
            </tr>
            <tr>
              <th class="seq-col">-</th>
              <th class="item-col">建设期/运营期</th>
              <th class="total-col">-</th>

              <th
                class="year-col period-type-col"
                :colspan="constructionPeriod"
                style="text-align: center"
              >
                建设期
              </th>
              <th
                class="year-col period-type-col"
                :colspan="operationPeriod"
                style="text-align: center"
              >
                运营期
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td class="seq-col">1</td>
              <td class="item-col">应收帐款</td>
              <td class="total-col">
                {{ formatNumber(workingCapital.receivables.total) }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`receivables-${yearIndex}`"
              >
                <span>{{
                  formatNumber(workingCapital.receivables.year[yearIndex] || 0)
                }}</span>
              </td>
            </tr>
            <tr>
              <td class="seq-col">2</td>
              <td class="item-col">存货</td>
              <td class="total-col">
                {{ formatNumber(workingCapital.inventory.total) }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`inventory-${yearIndex}`"
              >
                <span>{{
                  formatNumber(workingCapital.inventory.year[yearIndex] || 0)
                }}</span>
              </td>
            </tr>
            <tr>
              <td class="seq-col">3</td>
              <td class="item-col">现金</td>
              <td class="total-col">
                {{ formatNumber(workingCapital.cash.total) }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`cash-${yearIndex}`"
              >
                <span>{{
                  formatNumber(workingCapital.cash.year[yearIndex] || 0)
                }}</span>
              </td>
            </tr>
            <tr>
              <td class="seq-col">4</td>
              <td class="item-col">应付帐款</td>
              <td class="total-col">
                {{ formatNumber(workingCapital.payables.total) }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`payables-${yearIndex}`"
              >
                <span>{{
                  formatNumber(workingCapital.payables.year[yearIndex] || 0)
                }}</span>
              </td>
            </tr>
            <tr class="category-row">
              <td class="seq-col">5</td>
              <td class="item-col">流动资产</td>
              <td class="total-col">
                {{ formatNumber(workingCapital.currentAssets.total) }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`currentAssets-${yearIndex}`"
              >
                <span>{{
                  formatNumber(
                    workingCapital.currentAssets.year[yearIndex] || 0
                  )
                }}</span>
              </td>
            </tr>
            <tr class="category-row">
              <td class="seq-col">6</td>
              <td class="item-col">流动负债</td>
              <td class="total-col">
                {{ formatNumber(workingCapital.currentLiabilities.total) }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`currentLiabilities-${yearIndex}`"
              >
                <span>{{
                  formatNumber(
                    workingCapital.currentLiabilities.year[yearIndex] || 0
                  )
                }}</span>
              </td>
            </tr>
            <tr class="bold-row">
              <td class="seq-col"></td>
              <td class="item-col">流动资金需求</td>
              <td class="total-col">
                {{
                  formatNumber(workingCapital.workingCapitalRequirement.total)
                }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`workingCapitalRequirement-${yearIndex}`"
              >
                <span>{{
                  formatNumber(
                    workingCapital.workingCapitalRequirement.year[yearIndex] ||
                      0
                  )
                }}</span>
              </td>
            </tr>
            <tr class="category-row">
              <td class="seq-col"></td>
              <td class="item-col">流动资金贷款</td>
              <td class="total-col">
                {{ formatNumber(workingCapital.workingCapitalLoan.total) }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`workingCapitalLoan-${yearIndex}`"
              >
                <span>{{
                  formatNumber(
                    workingCapital.workingCapitalLoan.year[yearIndex] || 0
                  )
                }}</span>
              </td>
            </tr>
            <tr class="category-row">
              <td class="seq-col"></td>
              <td class="item-col">流动资金自有资金</td>
              <td class="total-col">
                {{ formatNumber(workingCapital.workingCapitalEquity.total) }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`workingCapitalEquity-${yearIndex}`"
              >
                <span>{{
                  formatNumber(
                    workingCapital.workingCapitalEquity.year[yearIndex] || 0
                  )
                }}</span>
              </td>
            </tr>
            <tr class="bold-row">
              <td class="seq-col"></td>
              <td class="item-col">自有流动资金增加额</td>
              <td class="total-col">
                {{ formatNumber(workingCapital.equityIncreaseThisYear.total) }}
              </td>
              <td
                class="year-col"
                v-for="(year, yearIndex) in calculationPeriod"
                :key="`equityIncreaseThisYear-${yearIndex}`"
              >
                <span>{{
                  formatNumber(
                    workingCapital.equityIncreaseThisYear.year[yearIndex] || 0
                  )
                }}</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- <div class="summary-section">
      <div class="summary-item">
        <span class="summary-label">流动资金需求合计:</span>
        <span class="summary-value">{{
          formatNumber(workingCapital.workingCapitalRequirement.total)
        }}</span>
        <span class="summary-unit">万元</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">流动资金贷款:</span>
        <span class="summary-value">{{
          formatNumber(workingCapital.workingCapitalLoan.total)
        }}</span>
        <span class="summary-unit">万元</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">流动资金自有资金:</span>
        <span class="summary-value">{{
          formatNumber(workingCapital.workingCapitalEquity.total)
        }}</span>
        <span class="summary-unit">万元</span>
      </div>
      <div class="summary-item">
        <span class="summary-label">自有流动资金增加额:</span>
        <span class="summary-value">{{
          formatNumber(workingCapital.equityIncreaseThisYear.total)
        }}</span>
        <span class="summary-unit">万元</span>
      </div>
    </div> -->
  </div>
</template>

<script>
import { workingCapital } from "@/utils/comp.js";

export default {
  name: "WorkingCapital",
  data() {
    const workingCapitalData =
      this.$store.state.projectData?.["流动资金估算"] || {};
    const revenueData =
      this.$store.state.projectData?.["营业收入税金及利润"]?.[
        "营业收入及营业税金"
      ]?.["revenue"] || {};
    const costData =
      this.$store.state.projectData?.["成本费用"]?.["经营成本"] || {};

    return {
      inputData: {
        // 可调整的参数
        accountsReceivableTurnover:
          workingCapitalData.accountsReceivableTurnover || 8, // 应收帐款年周转次数
        accountsPayableTurnover:
          workingCapitalData.accountsPayableTurnover || 12, // 应付帐款年周转次数
        inventoryTurnover: workingCapitalData.inventoryTurnover || 6, // 存货年周转次数
        cashTurnover: workingCapitalData.cashTurnover || 24, // 现金年周转次数
        loanRatio: workingCapitalData.loanRatio || 0, // 流动资金贷款比例(%)
        loanInterestRate: workingCapitalData.loanInterestRate || 4.35, // 流动资金贷款利率(%)
      },
    };
  },
  computed: {
    projectData() {
      return this.$store.state.projectData || {};
    },
    basicData() {
      return this.projectData["基础数据"] || {};
    },
    costData() {
      return this.projectData["成本费用"] || {};
    },
    calculationPeriod() {
      return this.basicData?.calculationPeriod || 15;
    },
    constructionPeriod() {
      return this.basicData?.constructionPeriod || 1;
    },
    operationPeriod() {
      return this.basicData?.operationPeriod || 14;
    },

    workingCapital() {
      // 调用计算函数
      const result = workingCapital();
      return result;
    },
  },
  methods: {
    formatNumber(num) {
      if (num === null || num === undefined || isNaN(num) || num === 0) return "";
      return Number(num)
        .toFixed(2)
        .replace(/\B(?=(\d{3})+(?!\d))/g, ",");
    },
    updateWorkingCapital() {
      // 获取当前的项目数据
      const currentProjectData = this.$store.state.projectData || {};

      // 确保流动资金估算部分存在
      if (!currentProjectData["流动资金估算"]) {
        currentProjectData["流动资金估算"] = {};
      }

      // 如果贷款比例为0，自动变为70%
      if (this.inputData.loanRatio < 0) {
        this.inputData.loanRatio = 0;
      }

      // 只更新流动资金估算数据，不覆盖其他组件的数据
      const updatedData = {
        ...currentProjectData,
        流动资金估算: {
          ...currentProjectData["流动资金估算"],
          ...this.inputData,
        },
      };

      // 写入自有流动资金本年增加额的合计
      if (this.workingCapital && this.workingCapital.equityIncreaseThisYear) {
        updatedData["总投资估算"] = updatedData["总投资估算"] || {};
        updatedData["总投资估算"]["总表"] = updatedData["总投资估算"]["总表"] || {};
        updatedData["总投资估算"]["总表"]["流动资金"] =
          updatedData["总投资估算"]["总表"]["流动资金"] || {};
        updatedData["总投资估算"]["总表"]["流动资金"]["其他费用"] =
          this.workingCapital.equityIncreaseThisYear.total;
        updatedData["总投资估算"]["总表"]["流动资金"]["合计"] =
          this.workingCapital.equityIncreaseThisYear.total;
      }

      // 保存到store，使用正确的mutation名称
      this.$store.commit("setProjectData", updatedData);

      // 触发重新计算
      this.$nextTick(() => {
        this.$forceUpdate();
      });
    },
  },
  mounted() {
    // 初始化时更新计算
    this.updateWorkingCapital();
  },
};
</script>

<style scoped>
.working-capital-container {
  background: #f8f9fa;
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
}

.input-area {
  margin-bottom: 20px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 15px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.input-row {
  display: flex;
  gap: 20px;
  flex-wrap: wrap;
}

.input-group {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  background: rgba(255, 255, 255, 0.95);
  padding: 8px 12px;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  vertical-align: top;
}

.input-label {
  font-weight: 600;
  color: #2c3e50;
  font-size: 12px;
  text-align: left;
}

.input-field {
  padding: 4px 6px;
  border: 1px solid #ced4da;
  border-radius: 4px;
  font-size: 12px;
  width: 80px;
  background: white;
  transition: all 0.3s ease;
}

.input-with-unit {
  display: flex;
  align-items: center;
  gap: 4px;
}

.input-field:focus {
  outline: none;
  border-color: #007bff;
  box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
}

.input-field.readonly {
  background-color: #f8f9fa;
  color: #6c757d;
  cursor: not-allowed;
}

.input-unit {
  color: #6c757d;
  font-size: 12px;
  min-width: 40px;
  font-weight: 500;
}

.table-wrapper {
  background: white;
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.table-scroll-area {
  overflow-x: auto;
  max-width: 100%;
}

.working-capital-table {
  width: 100%;
  min-width: 1200px;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 12px;
  border: 1px solid #e4e7ed;
}

.working-capital-table th,
.working-capital-table td {
  padding: 6px 1px;
  text-align: right;
  border: 0.5px solid #e4e7ed;
  white-space: nowrap;
}

.working-capital-table th {
  background-color: #f5f7fa;
  font-weight: 600;
  color: #606266;
  position: sticky;
  top: 0;
  z-index: 10;
  font-size: 13px;
}

.seq-col {
  width: 35px;
  text-align: center !important;
  font-weight: 500;
  font-size: 12px;
}

.item-col {
  width: 100px;
  text-align: left !important;
  padding-left: 4px;
  font-size: 12px;
}

.total-col {
  width: 80px;
  font-weight: 600;
  background-color: #f8f9fa;
  font-size: 12px;
}

.year-col {
  width: 50px;
  font-size: 12px;
}

.period-type-col {
  background-color: #e9ecef;
  font-size: 12px;
  color: #6c757d;
  text-align: center;
}

.category-row {
  background-color: #f8f9fa;
}

.category-row .total-col {
  background-color: #e9ecef;
  font-weight: 600;
}

.detail-row {
  background-color: #ffffff;
}

.bold-row {
  background-color: #d4edda;
  font-weight: 600;
}

.bold-row .total-col {
  background-color: #c3e6cb;
  font-weight: 700;
}

.number-column {
  text-align: right;
}

.summary-section {
  margin-top: 20px;
  display: flex;
  gap: 30px;
  flex-wrap: wrap;
}

.summary-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  background: white;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.summary-label {
  font-weight: 500;
  color: #495057;
}

.summary-value {
  font-weight: 700;
  color: #007bff;
  font-size: 16px;
}

.summary-unit {
  color: #6c757d;
  font-size: 14px;
}

/* 响应式设计 */
@media (max-width: 768px) {
  .input-row {
    flex-direction: column;
    gap: 10px;
  }

  .input-group {
    justify-content: space-between;
  }

  .summary-section {
    flex-direction: column;
    gap: 10px;
  }

  .working-capital-table th,
  .working-capital-table td {
    padding: 8px 4px;
    font-size: 12px;
  }

  .seq-col {
    width: 40px;
  }

  .item-col {
    width: 100px;
  }

  .total-col {
    width: 80px;
  }

  .year-col {
    width: 60px;
  }
}
</style>
